import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tree, Spin } from 'antd';
import styles from '@/views/dictionary/index.module.less';
import MeddraCateTreeIcon from '@/views/dictionary/components/dictTree/meddraCateTreeIcon';

/**
 * TablePopover 组件的属性接口
 * @interface TablePopoverProps
 * @property {boolean} visible - 控制 popover 的显示/隐藏
 * @property {any} record - 当前行的数据记录
 * @property {HTMLElement | null} triggerElement - 触发 popover 的 DOM 元素
 * @property {() => void} onClose - 关闭 popover 的回调函数
 * @property {(record: any) => React.ReactNode} [renderContent] - 自定义渲染 popover 内容的函数
 * @property {boolean} [isFixed] - 是否固定显示（点击触发），默认为 false（悬浮触发）
 * @property {any[]} [treeData] - 树形数据
 * @property {boolean} [isMedDRA] - 是否为 MedDRA 数据，用于控制图标显示
 */
interface TablePopoverProps {
  visible: boolean;
  record: any;
  triggerElement: HTMLElement | null;
  onClose: () => void;
  renderContent?: (record: any) => React.ReactNode;
  isFixed?: boolean;
  treeData?: any[];
  isMedDRA?: boolean;
}

/**
 * 获取或创建全局的 popover 容器
 * 这个容器会被添加到 body 中，用于承载所有的 popover
 * 使用绝对定位和 visible overflow 来确保 popover 不会被其他元素的 overflow: hidden 影响
 */
const getPopoverContainer = () => {
  let container = document.getElementById('ikg-popover-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'ikg-popover-container';
    // 设置容器样式
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '0';
    container.style.overflow = 'visible';
    container.style.pointerEvents = 'none'; // 防止容器本身影响鼠标事件
    document.body.appendChild(container);
  }
  return container;
};

/**
 * TablePopover 组件
 * 用于在表格中显示悬浮或固定的弹出层，支持树形结构展示
 * 使用 createPortal 将内容渲染到全局容器中，避免被表格的 overflow: hidden 影响
 */
const TablePopover: React.FC<TablePopoverProps> = ({
  visible,
  record,
  triggerElement,
  onClose,
  renderContent,
  isFixed = false,
  treeData,
  isMedDRA = false,
}) => {
  // 引用 popover 元素，用于判断点击是否在 popover 内
  const popoverRef = useRef<HTMLDivElement>(null);
  // 存储 popover 的位置信息
  const [position, setPosition] = useState({ top: 0, left: 0 });

  /**
   * 监听触发元素位置变化
   * 在滚动和窗口大小改变时更新 popover 位置
   */
  useEffect(() => {
    if (triggerElement && visible) {
      const updatePosition = () => {
        const rect = triggerElement.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

        setPosition({
          top: rect.top + scrollTop + rect.height / 2, // 垂直居中对齐
          left: rect.left + scrollLeft, // 水平对齐
        });
      };

      updatePosition();

      // 添加滚动和调整大小的事件监听
      window.addEventListener('scroll', updatePosition, true); // true 表示在捕获阶段处理
      window.addEventListener('resize', updatePosition);

      return () => {
        // 清理事件监听
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [triggerElement, visible]);

  /**
   * 处理固定模式下的点击事件
   * 点击 popover 和触发元素之外的区域时关闭 popover
   */
  useEffect(() => {
    if (visible && isFixed) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (
          !popoverRef.current?.contains(target) && // 不是点击在 popover 内
          !triggerElement?.contains(target) && // 不是点击在触发元素上
          !target.closest('.ant-tree-node-content-wrapper') // 不是点击在树节点上
        ) {
          onClose();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [visible, isFixed, triggerElement]);

  /**
   * 渲染 MedDRA 图标
   * @param level - 层级
   * @param primarySocFg - 主要 SOC 标志
   */
  const renderIcon = (level: number, primarySocFg: string) => {
    if (!isMedDRA) return null;
    return (
      <span className={styles.dictMeddraIcon}>
        <MeddraCateTreeIcon bordered={primarySocFg === 'Y'} level={level} isForward={false} />
      </span>
    );
  };

  /**
   * 渲染树形内容
   * 包括加载状态、空状态处理
   */
  const renderTreeContent = () => {
    if (!treeData) return <Spin />;
    if (treeData.length === 0) return null;
    return (
      <div 
        className="ikg-popover-content"
        style={{ 
          position: 'absolute',
          left: `${position.left}px`,
          top: `${position.top}px`,
          transform: 'translate(-100%, -50%)', // 向左偏移自身宽度，向上偏移一半高度
          marginRight: '8px',
          background: '#fff',
          boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
          borderRadius: '4px',
          padding: '12px',
          minHeight: '100px',
          width: '320px',
          maxHeight: '300px',
          overflow: 'auto',
          pointerEvents: 'auto', // 恢复鼠标事件，覆盖容器的 pointerEvents: none
        }}
      >
        <Tree
          defaultExpandAll
          titleRender={(node: any) => (
            <div>
              {renderIcon(node.level, node.primarySocFg)}
              {`${node.code || ''} ${node.name || ''}`}
            </div>
          )}
          key={record.id}
          treeData={treeData}
          fieldNames={{ title: 'name', key: 'key', children: 'children' }}
          onClick={(e) => {
            e.stopPropagation(); // 阻止树节点点击事件冒泡
          }}
        />
      </div>
    );
  };

  // 获取要渲染的内容，支持自定义渲染
  const content = renderContent ? renderContent(record) : renderTreeContent();

  // 不可见或没有触发元素时不渲染
  if (!visible || !triggerElement) return null;

  // 使用 createPortal 将内容渲染到全局容器中
  return createPortal(
    <div 
      ref={popoverRef}
      className="ikg-popover"
      onMouseEnter={(e) => {
        if (!isFixed) {
          e.stopPropagation(); // 阻止鼠标进入事件冒泡
        }
      }}
      onMouseLeave={(e) => {
        if (!isFixed) { // 只在悬浮模式下处理鼠标离开事件
          const relatedTarget = e.relatedTarget as HTMLElement;
          const isLeavingToPopover = relatedTarget?.closest('.ikg-popover-content');
          const isLeavingToTrigger = relatedTarget?.closest('.ikg-icon-tooltip-triggle');
          
          // 如果不是移动到 popover 内部或触发元素上，则关闭 popover
          if (!isLeavingToPopover && !isLeavingToTrigger) {
            onClose();
          }
        }
      }}
    >
      {content}
    </div>,
    getPopoverContainer()
  );
};

export default TablePopover; 